import { createContext, useContext, ReactNode, useEffect } from "react";
import { FeatureFlags } from "../types";

type FeatureFlagsContextProps = {
  flags: FeatureFlags;
  experiencesRawString?: string;
};

export const createFeatureFlagsContext = () => {
  const Context = createContext<FeatureFlagsContextProps | undefined>(
    undefined,
  );

  const FeatureFlagsProvider = ({
    children,
    flags,
    experiencesRawString,
    id,
  }: {
    children: ReactNode;
    flags: FeatureFlags;
    experiencesRawString?: string;
    id: string;
  }) => {
    useEffect(
      function exposeFeatureFlagsOnWindow() {
        window.featureFlags = window.featureFlags || {};
        window.featureFlags[id] = flags;
      },
      [id, flags],
    );

    useEffect(function exposeListFlagsDebugger() {
      window.listFlags = (inputId) => {
        if (window.featureFlags[inputId]) {
          // eslint-disable-next-line no-console
          console.table(
            Object.entries(window.featureFlags[inputId]).flatMap<
              Record<string, unknown>
            >(([name, flag]) => {
              if (flag.source !== "monetate") {
                return [{ name, ...flag }];
              }

              return [flag, ...(flag.additionalExperiences ?? [])].map(
                (experience) => ({
                  name,
                  source: experience.source,
                  value: experience.value,
                  experienceName: experience.experienceName,
                  experienceId: experience.experienceId,
                  impressionId: experience.impressionId,
                  variantLabel: experience.variantLabel,
                }),
              );
            }),
          );
        }
        if (
          !inputId ||
          Object.keys(window.featureFlags).indexOf(inputId) === -1
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            `listFlags called without correct context id. Please provide one of "${Object.keys(window.featureFlags).join(", ")}" to see the current flag settings for that context.`,
          );
        }
      };
    }, []);

    return (
      <Context.Provider value={{ flags, experiencesRawString }}>
        {children}
      </Context.Provider>
    );
  };

  const useFeatureFlags = (): FeatureFlags => {
    const context = useContext(Context);
    if (!context) {
      throw new Error(
        "useFeatureFlags must be used within a FeatureFlagsProvider",
      );
    }
    return context.flags;
  };

  const useExperienceString = () => {
    const context = useContext(Context);
    if (!context) {
      throw new Error(
        "useExperienceString must be used within a FeatureFlagsProvider",
      );
    }
    return context.experiencesRawString;
  };

  return {
    Provider: FeatureFlagsProvider,
    useFeatureFlags,
    useExperienceString,
  };
};
